import { WizardReportConfig } from '../services/agent-builder.interface';

/**
 * Ties the downloadable-report template builder to async S3 reports.
 *
 * An automation that delivers to S3 (`s3`, `s3+email`, `s3+teams`) produces a
 * durable report artifact, so it should get the SAME report-template builder a
 * chat report agent gets (shape.has_report_artifact + meta.report) — the
 * creator customizes the report's title, sections and theme instead of being
 * stuck with the default template. Email-only / Teams-only deliveries are
 * notification-style messages with no durable artifact, so they stay plain.
 *
 * This module is the single source of truth for that rule, shared by every
 * creation surface (starter template, AI hero create, the wizard's delivery
 * picker). The co-builder + ai-assist backends mirror it in their prompts.
 */

/** Delivery kinds that write a durable report to the reports bucket. */
export const S3_DELIVERY_KINDS = ['s3', 's3+email', 's3+teams'] as const;

/** True when a delivery kind uploads a report artifact to S3 (vs. a plain
 *  email/Teams notification). */
export function deliveryWritesReport(kind: string | undefined | null): boolean {
  return !!kind && (S3_DELIVERY_KINDS as readonly string[]).includes(kind);
}

/** A starter report template seeded when an S3 automation first gains the
 *  report-template builder, so the builder opens visibly populated and
 *  editable. Title is left empty — the renderer falls back to the agent's
 *  display name; the creator (or the co-builder) refines the rest. */
export function starterReportConfig(): WizardReportConfig {
  return {
    title: '',
    sections: ['Summary', 'Key findings', 'Details'],
    format: 'html',
  };
}

/** True when a report config has no meaningful content yet (so it's safe to
 *  seed a starter template without clobbering the creator's work). */
export function reportConfigIsEmpty(report: WizardReportConfig | undefined | null): boolean {
  if (!report) return true;
  return !(
    report.markdown?.trim() ||
    report.sections?.length ||
    report.title?.trim() ||
    report.description?.trim() ||
    report.dashboard?.sections?.length
  );
}
